import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {
  Button,
  Flags,
  Icons,
  type IFlagProps,
  Option,
  Select,
  SingleDatePicker,
  Sizes,
} from '@sede-enterprise/shell-ds-react-framework';
import { CET_TIME_ZONE, COMMON_MESSAGES } from 'src/constants/Common';
import routepaths from 'src/router/routepaths';
import { HealthCheckContainer } from './style';
import { COUNTRY_HEALTH_DATA, MATCHING_STATUS_OPTIONS, UNIT_LABEL } from './constants';
import { ICountryHealth } from './types';

dayjs.extend(utc);
dayjs.extend(timezone);

const { Star, StarSolid, LineChartSolid, IconCircleInfo, CheckCircleSolid, CrossCircleSolid } =
  Icons;

const FLAG_SIZE = 20;
const NET_EXPOSURE_DECIMAL_PLACES = 3;
const FAVORITE_STAR_COLOR = '#FBCE07';

const sortCountriesByName = (countries: ICountryHealth[]) =>
  [...countries].sort((countryA, countryB) => countryA.name.localeCompare(countryB.name));

const getCountryOrderByFavorites = (
  countries: ICountryHealth[],
  favoriteMap: Record<string, boolean>,
) => {
  const pinnedCountries = countries.filter((country) => favoriteMap[country.id]);
  const unpinnedCountries = sortCountriesByName(
    countries.filter((country) => !favoriteMap[country.id]),
  );

  return [...pinnedCountries, ...unpinnedCountries];
};

const getDefaultCountryOrder = () => {
  const defaultFavorites = COUNTRY_HEALTH_DATA.reduce<Record<string, boolean>>((acc, country) => {
    acc[country.id] = country.favorite;
    return acc;
  }, {});

  return getCountryOrderByFavorites(COUNTRY_HEALTH_DATA, defaultFavorites);
};

const renderFlag = (flagCode: string) => {
  const FlagComponent = (Flags as Record<string, React.FC<IFlagProps>>)[flagCode];
  if (!FlagComponent) {
    return null;
  }
  return <FlagComponent height={FLAG_SIZE} width={FLAG_SIZE} />;
};

const HealthCheck = () => {
  const navigate = useNavigate();
  const [matchingStatus, setMatchingStatus] = useState<string>('all');
  const [gasDay, setGasDay] = useState<string>(() =>
    dayjs().tz(CET_TIME_ZONE).format(COMMON_MESSAGES.DATE_YYYY_MM_DD),
  );
  const [order, setOrder] = useState<ICountryHealth[]>(getDefaultCountryOrder);
  const [favorites, setFavorites] = useState<Record<string, boolean>>(() =>
    COUNTRY_HEALTH_DATA.reduce<Record<string, boolean>>((acc, country) => {
      acc[country.id] = country.favorite;
      return acc;
    }, {}),
  );
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);

  const filteredCountries = useMemo(
    () =>
      order.filter((country) => {
        const isStatusMatch =
          matchingStatus === 'all' ||
          country[matchingStatus as 'mismatch' | 'pending' | 'matched'] > 0;

        return isStatusMatch;
      }),
    [order, matchingStatus],
  );

  const healthSummary = useMemo(() => {
    const totals = filteredCountries.reduce(
      (summary, country) => ({
        netExposure: summary.netExposure + Number.parseFloat(country.netValueLabel),
        awaitingConfirmations: summary.awaitingConfirmations + country.pending,
        mismatch: summary.mismatch + country.mismatch,
        matched: summary.matched + country.matched,
      }),
      { netExposure: 0, awaitingConfirmations: 0, mismatch: 0, matched: 0 },
    );

    return {
      ...totals,
      netExposureValueLabel: `${Number(totals.netExposure.toFixed(NET_EXPOSURE_DECIMAL_PLACES))}M`,
    };
  }, [filteredCountries]);

  const toggleFavorite = (id: string) => {
    const nextFavorites = { ...favorites, [id]: !favorites[id] };

    setOrder((currentOrder) => getCountryOrderByFavorites(currentOrder, nextFavorites));
    setFavorites(nextFavorites);
  };

  // Reorder non-starred cards while keeping starred (locked) cards in place.
  const reorderCountries = (fromId: string, toId: string) => {
    setOrder((prev) => {
      const movable = prev.filter((country) => !favorites[country.id]);
      const fromPos = movable.findIndex((country) => country.id === fromId);
      const toPos = movable.findIndex((country) => country.id === toId);
      if (fromPos === -1 || toPos === -1) {
        return prev;
      }
      const [moved] = movable.splice(fromPos, 1);
      movable.splice(toPos, 0, moved);
      let movableIndex = 0;
      return prev.map((country) => {
        if (favorites[country.id]) {
          return country;
        }

        const movableCountry = movable[movableIndex];
        movableIndex += 1;
        return movableCountry;
      });
    });
  };

  const handleCardKeyDown = (id: string) => (event: React.KeyboardEvent<HTMLDivElement>) => {
    const moveForward = event.key === 'ArrowRight' || event.key === 'ArrowDown';
    const moveBackward = event.key === 'ArrowLeft' || event.key === 'ArrowUp';
    if (favorites[id] || (!moveForward && !moveBackward)) {
      return;
    }

    const movableCountries = order.filter((country) => !favorites[country.id]);
    const currentIndex = movableCountries.findIndex((country) => country.id === id);
    const targetCountry = movableCountries[currentIndex + (moveForward ? 1 : -1)];
    if (!targetCountry) {
      return;
    }

    event.preventDefault();
    reorderCountries(id, targetCountry.id);
  };

  const handleDragStart = (id: string) => (event: React.DragEvent<HTMLDivElement>) => {
    if (favorites[id]) {
      event.preventDefault();
      return;
    }
    setDraggedId(id);
    event.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (id: string) => (event: React.DragEvent<HTMLDivElement>) => {
    if (!draggedId || favorites[id] || id === draggedId) {
      return;
    }
    event.preventDefault();
    setDragOverId(id);
  };

  const handleDrop = (id: string) => (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (draggedId && !favorites[id] && draggedId !== id) {
      reorderCountries(draggedId, id);
    }
    setDraggedId(null);
    setDragOverId(null);
  };

  const handleDragEnd = () => {
    setDraggedId(null);
    setDragOverId(null);
  };

  const handleInvestigate = (country: ICountryHealth) => {
    const query = country.marketId == null ? '' : `?marketId=${country.marketId}`;
    navigate(`${routepaths.newGenesisPositionsMarketPosition}${query}`);
  };

  const renderCard = (country: ICountryHealth) => {
    const isFavorite = favorites[country.id];
    const cardClassName = [
      'country-card',
      isFavorite ? 'locked' : '',
      draggedId === country.id ? 'dragging' : '',
      dragOverId === country.id ? 'drag-over' : '',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div
        className={cardClassName}
        key={country.id}
        data-testid={`health-card-${country.id}`}
        role='button'
        tabIndex={isFavorite ? -1 : 0}
        aria-label={`Reorder ${country.name}. Use arrow keys to move.`}
        draggable={!isFavorite}
        onKeyDown={handleCardKeyDown(country.id)}
        onDragStart={handleDragStart(country.id)}
        onDragOver={handleDragOver(country.id)}
        onDragLeave={() => setDragOverId((prev) => (prev === country.id ? null : prev))}
        onDrop={handleDrop(country.id)}
        onDragEnd={handleDragEnd}
      >
        <div className='country-card-header'>
          <div className='country-name'>
            {renderFlag(country.flagCode)}
            <span>{country.name}</span>
          </div>
          <Button
            className={`favorite-btn${isFavorite ? ' is-favorite' : ''}`}
            aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
            onClick={() => toggleFavorite(country.id)}
            variant='transparent'
            icon={
              isFavorite ? (
                <StarSolid
                  style={{
                    color: FAVORITE_STAR_COLOR,
                    fill: FAVORITE_STAR_COLOR,
                    stroke: FAVORITE_STAR_COLOR,
                  }}
                />
              ) : (
                <Star />
              )
            }
            iconOnly
          />
        </div>

        <div className='country-card-body'>
          <span className='net-label'>Net</span>
          <div className={`net-value net-value-${country.trend}`}>
            <span className='net-amount'>{country.netValueLabel}</span>
            <span className='net-unit'>{UNIT_LABEL}</span>
          </div>
        </div>

        <div className='country-card-stats'>
          <div className='stat'>
            <span className='stat-count-row'>
              <span className='stat-count'>{country.mismatch}</span>
              <CrossCircleSolid className='stat-icon stat-icon-mismatch' />
            </span>
            <span className='stat-label'>Mismatch</span>
          </div>
          <div className='stat'>
            <span className='stat-count-row'>
              <span className='stat-count'>{country.pending}</span>
              <IconCircleInfo className='stat-icon stat-icon-pending' />
            </span>
            <span className='stat-label'>Pending</span>
          </div>
          <div className='stat'>
            <span className='stat-count-row'>
              <span className='stat-count'>{country.matched}</span>
              <CheckCircleSolid className='stat-icon stat-icon-matched' />
            </span>
            <span className='stat-label'>Matched</span>
          </div>
        </div>

        <button
          type='button'
          className='investigate-btn'
          onClick={() => handleInvestigate(country)}
        >
          Investigate
        </button>
      </div>
    );
  };

  return (
    <HealthCheckContainer>
      <div className='filters-bar'>
        <div className='filter-field'>
          <span className='filter-label'>Gas Day</span>
          <SingleDatePicker
            size={Sizes.Small}
            format='DD MMMM YY'
            allowClear={false}
            value={dayjs(gasDay)}
            onChange={(val) => setGasDay(dayjs(val).format('YYYY-MM-DD'))}
            data-testid='health-check-gasday'
          />
        </div>
        <div className='filter-field'>
          <span className='filter-label'>Matching Status</span>
          <Select
            size={Sizes.Small}
            value={matchingStatus}
            allowClear={false}
            showSearch={false}
            placeholder='Placeholder'
            onChange={(val) => setMatchingStatus(val as string)}
            data-testid='health-check-matching-status'
          >
            {MATCHING_STATUS_OPTIONS.map((option) => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </div>
      </div>

      <div className='summary-bar'>
        <div className='summary-card'>
          <LineChartSolid className='summary-icon summary-icon-exposure' />
          <div className='summary-text'>
            <span className='summary-value'>
              {healthSummary.netExposureValueLabel}
              <span className='summary-unit'>{UNIT_LABEL}</span>
            </span>
            <span className='summary-label'>Net Exposure</span>
          </div>
        </div>
        <div className='summary-card'>
          <IconCircleInfo className='summary-icon summary-icon-awaiting' />
          <div className='summary-text'>
            <span className='summary-value'>{healthSummary.awaitingConfirmations}</span>
            <span className='summary-label'>Pending Confirmations</span>
          </div>
        </div>
        <div className='summary-card'>
          <CheckCircleSolid className='summary-icon summary-icon-matched' />
          <div className='summary-text'>
            <span className='summary-value'>{healthSummary.matched}</span>
            <span className='summary-label'>Matches</span>
          </div>
        </div>
        <div className='summary-card'>
          <CrossCircleSolid className='summary-icon summary-icon-mismatch' />
          <div className='summary-text'>
            <span className='summary-value'>{healthSummary.mismatch}</span>
            <span className='summary-label'>Mismatches</span>
          </div>
        </div>
      </div>

      <div className='country-grid'>{filteredCountries.map(renderCard)}</div>
    </HealthCheckContainer>
  );
};

export default HealthCheck;
